import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, User, Building2, AlertTriangle, Package, Sparkles, Loader2 } from "lucide-react";

const ROLES = ["Owner","CEO","Doctor","Practice Manager","Office Manager","CFO","IT Director","NP/PA","Other"];
const SOURCES = ["Website","Referral","Software Finder","Conference","Gartner","Software Advice","Ampliz","Cold Call"];
const RATINGS = [
  { v: "Hot", color: "var(--red)", bg: "var(--red-light)" },
  { v: "Warm", color: "var(--amber)", bg: "var(--amber-light)" },
  { v: "Cold", color: "var(--blue)", bg: "var(--blue-light)" },
  { v: "Dormant", color: "var(--gray)", bg: "var(--gray-light)" },
];
const TIMEZONES = ["EST","EDT","CST","MDT","PST","MST","AKST","HST"];
const PRACTICE_TYPES = ["New Practice","Existing Practice","Hybrid"];
const IMPL_TIMELINES = ["ASAP (<30d)","1-2 months","2-4 months","5-6 months","7+ months"];
const SPECIALTIES = ["Family Medicine","Internal Medicine","Pediatrics","Geriatrics","Cardiology","Neurology","Orthopedics","Psychiatry","Oncology","OB/GYN","Urgent Care","Dermatology","ENT","Pain Management","Behavioral Health","Other"];
const CURRENT_EHRS = ["Epic","athenahealth","NextGen","eClinicalWorks","Allscripts","Kareo","Legacy/Outdated","None/Paper","Other"];
const PAIN_POINTS = [
  { id: "charting", label: "Charting takes too long", solution: "AI EHR / MDNet" },
  { id: "front_desk", label: "Front desk inefficiency", solution: "Practice Management" },
  { id: "denials", label: "High claim denials", solution: "Medical Billing + RevQ" },
  { id: "ccm", label: "Chronic care management gaps", solution: "CCM / APCM" },
  { id: "rpm", label: "Remote patient tracking", solution: "RPM" },
  { id: "engagement", label: "Patient disengagement", solution: "REACH / Patient Engagement" },
  { id: "preventive", label: "Preventive care lapses", solution: "AWV Program" },
  { id: "mips", label: "MIPS / Quality reporting anxiety", solution: "MIPS Advisory" },
  { id: "fragmented", label: "Fragmented systems", solution: "All-in-One Platform" },
  { id: "paper", label: "Paper / manual workflows", solution: "Digital transformation suite" },
];
const PRODUCTS = ["AI-powered EHR (MDNet)","Practice Management","Medical Billing / RCM","RevQ AI Billing","CCM","RPM","APCM","AWV Program","REACH / Patient Engagement","Telemedicine","EPCS","AI Scribe Only"];
const BUDGETS = ["Below $200","$200-$300","$300-$400","$400-$500","$500-$600","Above $600","Open/Flexible","Price Sensitive","High Budget"];
const PRICING_FIT = ["Within Budget","Above Budget","Negotiable","Not Provided"];
const NEXT_ACTIONS = ["Send Quote + Resources","Schedule Follow-Up Demo","Follow-Up Call/Email","Proposal Under Review","Pricing Discussion/Negotiation","Internal Decision Pending","Awaiting Client Input","No Clear Next Step"];
const INTEREST_LEVELS = ["Very Interested","Moderately Interested","Low Interest","Not Interested","No Response","Revisit Later"];
const ENGAGEMENT_LEVELS = ["Highly Engaged","Moderately Engaged","Low Engagement","No Engagement"];

function Chip({ label, selected, onClick, color, bg }) {
  const s = selected ? (color ? `selected-${color}` : "selected") : "";
  return (
    <button
      type="button"
      className={`chip ${s}`}
      style={selected && bg ? { background: bg, color: color, borderColor: color } : {}}
      onClick={onClick}
    >{label}</button>
  );
}

export default function AddLead() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [type, setType] = useState("lead");
  // Iter 167.g — customer bucket (only used when type=client). Defaults
  // to "implementation" so brand-new customers land in the tracker.
  const [bucket, setBucket] = useState("implementation");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [form, setForm] = useState({
    contact_name: "", company: "", phone: "", email: "",
    roles: [], source: "", rating: "", timezone: "EST",
    practice_type: "", impl_timeline: "", specialties: [], provider_md: "1", provider_np: "0", provider_pa: "0", current_ehrs: [],
    pain_points: [], pain_notes: "",
    products: [], budget: "", pricing_fit: "", demo_date: "", expected_close: "", decision_timeline: "", next_action: "",
    interest_level: "", engagement_level: "", notes: "",
    city: "", state: "",
  });

  const u = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const toggle = (field, value) => {
    const arr = form[field];
    u(field, arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
  };

  const fetchAI = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/ai/lead-score`, {
        contact_name: form.contact_name, company: form.company,
        specialty: form.specialties.join(", "), providers: `MD:${form.provider_md} NP:${form.provider_np} PA:${form.provider_pa}`,
        current_ehr: form.current_ehrs.join(", "), pain_points: form.pain_points,
        products: form.products, budget: form.budget, timeline: form.decision_timeline,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAiResult(res.data);
    } catch {
      setAiResult({ ai_score: 65, temperature: "Warm", top_products: [
        { product: "AI-powered EHR (MDNet)", match_pct: 92, reason: "Addresses charting and documentation needs" },
        { product: "Practice Management", match_pct: 85, reason: "Streamlines front desk operations" },
        { product: "Medical Billing / RCM", match_pct: 78, reason: "Reduces claim denials" },
      ], next_action: "Schedule a personalized demo", key_insight: "Strong fit for all-in-one platform migration" });
    }
    setAiLoading(false);
  };

  const [errors, setErrors] = useState({});

  const validateStep1 = () => {
    const e = {};
    if (!form.contact_name.trim()) e.contact_name = "Contact name is required";
    if (!form.company.trim()) e.company = "Company / Practice name is required";
    setErrors(e);
    if (Object.keys(e).length > 0) toast.error("Please complete required fields before saving");
    return Object.keys(e).length === 0;
  };

  const saveLead = async () => {
    if (!form.contact_name.trim() || !form.company.trim()) {
      toast.error("Please complete required fields before saving");
      return;
    }
    if (type === "client" && form.products.length === 0) {
      toast.error("Please select at least one product for the client");
      return;
    }
    setSaving(true);
    try {
      const contactRes = await axios.post(`${API}/contacts`, {
        first_name: form.contact_name.split(" ")[0] || form.contact_name,
        last_name: form.contact_name.split(" ").slice(1).join(" ") || "",
        email: form.email, phone: form.phone, company: form.company,
        job_title: form.roles.join(", "), industry: "Healthcare",
        pain_points: form.pain_points, tags: form.products,
        notes: form.notes, source: form.source, status: type === "client" ? "live_client" : "active",
        // Iter 167.g (Danny) — stamp bucket + initial enrolled_products
        // when the user explicitly added an "Existing Customer". Without
        // this, _derive_bucket defaults to "live" (the migration
        // fallback for un-tagged legacy contacts), which mis-categorized
        // brand-new customers that should be in Implementation.
        ...(type === "client" ? {
          bucket,
          enrolled_products: (form.products || []).map(p => ({
            product_id: p,
            status: bucket === "live" ? "live" : "pending",
            enrolled_date: new Date().toISOString(),
          })),
          // For Implementation customers, auto-stamp a kickoff date so
          // the "Days since kickoff" counter starts immediately.
          ...(bucket === "implementation" ? {
            implementation: { kickoff_at: new Date().toISOString() },
          } : {}),
        } : {}),
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (type !== "client") {
        await axios.post(`${API}/leads`, {
          contact_id: contactRes.data.id, title: form.company || form.contact_name,
          value: 0, stage: "new_lead", probability: aiResult?.ai_score || 10,
          expected_close_date: form.expected_close, product_interest: form.products,
          notes: form.notes, lead_source: form.source.toLowerCase().replace(/\s+/g, "_"),
          demo_date: form.demo_date, interest_level: form.rating?.toLowerCase(),
          contact_name: form.contact_name, company: form.company,
          // Iter 179.1 (Tatiana #cd6679ef Feb 2026) — `email` and `phone`
          // were only being stamped on the contact, not the lead doc.
          // Lead Focus reads `lead.email` / `lead.phone` directly, so
          // the page rendered "—" even though the rep entered both
          // during creation. Persist them on the lead too.
          email: form.email || null, phone: form.phone || null,
          specialty: form.specialties.join(", "), budget_range: form.budget,
          ai_score: aiResult?.ai_score || 0,
          city: form.city || null, state: form.state || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(type === "client" ? `Customer added to ${bucket === "live" ? "Live" : "Implementation"}` : "Lead saved to pipeline");
      navigate(type === "client" ? "/customers" : "/leads");
    } catch {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  const canNext = step === 1 ? form.contact_name && form.company : true;

  return (
    <div data-testid="add-lead-page">
      <div className="topbar">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button className="btn" onClick={() => navigate(-1)}><ArrowLeft style={{ width: 14, height: 14 }} /> Back</button>
          <div>
            <div style={{ fontSize: 14, fontWeight: 650 }}>Add Lead / Client</div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Step {step} of {type === "client" ? 3 : 5}</div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="wizard-container">
          {/* Type Selector */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
            {[
              { id: "lead", label: "New Lead / Prospect", desc: "Goes into pipeline" },
              // Iter 167.g — renamed "Existing Live Client" → "Existing Customer"
              // so the form language matches the sidebar rename.
              { id: "client", label: "Existing Customer", desc: "Direct to customers" },
              { id: "referral", label: "Referral Contact", desc: "Added as referral source" },
            ].map(t => (
              <button key={t.id} className="card" onClick={() => setType(t.id)} data-testid={`type-${t.id}`}
                style={{ textAlign: "center", cursor: "pointer", border: type === t.id ? "1.5px solid var(--blue)" : undefined, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: type === t.id ? "var(--blue)" : "var(--text-primary)" }}>{t.label}</div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>{t.desc}</div>
              </button>
            ))}
          </div>

          {/* Iter 167.g (Danny — Customers Section Overhaul) — bucket pill
              selector only renders when type=client. Determines whether
              the new customer lands in the Live bucket (steady-state)
              or the Implementation bucket (in onboarding). Defaults to
              "implementation" since a NEW customer that goes through
              this form is almost always still being onboarded. */}
          {type === "client" && (
            <div style={{ marginBottom: 20, padding: "12px 16px", background: "var(--surface)", border: "0.5px solid var(--border)", borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 6 }}>
                Customer bucket
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { id: "implementation", label: "Implementation", desc: "Still being onboarded · tracker activates" },
                  { id: "live", label: "Live", desc: "All products in steady state" },
                ].map(b => (
                  <button
                    key={b.id}
                    data-testid={`customer-bucket-${b.id}`}
                    onClick={() => setBucket(b.id)}
                    type="button"
                    className="btn"
                    style={{
                      flex: 1, padding: "10px 12px", borderRadius: 8, textAlign: "left",
                      background: bucket === b.id ? "var(--blue-light)" : "transparent",
                      border: `0.5px solid ${bucket === b.id ? "var(--blue)" : "var(--border)"}`,
                      color: bucket === b.id ? "var(--blue)" : "var(--text-primary)",
                    }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{b.label}</div>
                    <div style={{ fontSize: 10, color: bucket === b.id ? "var(--blue)" : "var(--text-tertiary)", marginTop: 2 }}>{b.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Progress */}
          <div className="wizard-progress">
            {(type === "client"
              ? ["1 Who?", "2 Practice", "3 Notes"]
              : ["1 Who?", "2 Practice", "3 Pain Points", "4 Products", "5 AI Insights"]
            ).map((s, i) => (
              <div key={i} className={`wizard-step ${i + 1 === step ? "active" : i + 1 < step ? "completed" : ""}`}>{s}</div>
            ))}
          </div>

          <div className="wizard-card">
            {/* Step 1 */}
            {step === 1 && (
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Contact Information</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div><label style={{ fontSize: 11, color: errors.contact_name ? "var(--red)" : "var(--text-tertiary)" }}>Contact Name *</label>
                    <input className="input" value={form.contact_name} onChange={e => { u("contact_name", e.target.value); setErrors(prev => ({...prev, contact_name: undefined})); }} data-testid="input-name"
                      style={errors.contact_name ? { borderColor: "var(--red)", boxShadow: "0 0 0 2px rgba(170,42,42,0.12)" } : {}} /></div>
                  <div><label style={{ fontSize: 11, color: errors.company ? "var(--red)" : "var(--text-tertiary)" }}>Company / Practice *</label>
                    <input className="input" value={form.company} onChange={e => { u("company", e.target.value); setErrors(prev => ({...prev, company: undefined})); }} data-testid="input-company"
                      style={errors.company ? { borderColor: "var(--red)", boxShadow: "0 0 0 2px rgba(170,42,42,0.12)" } : {}} /></div>
                  <div><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Phone</label>
                    <input className="input" type="tel" value={form.phone} onChange={e => u("phone", e.target.value)} /></div>
                  <div><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Email</label>
                    <input className="input" type="email" value={form.email} onChange={e => u("email", e.target.value)} /></div>
                </div>
                <div style={{ marginTop: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Role</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{ROLES.map(r => <Chip key={r} label={r} selected={form.roles.includes(r)} onClick={() => toggle("roles", r)} />)}</div>
                </div>
                <div style={{ marginTop: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Lead Source</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{SOURCES.map(s => <Chip key={s} label={s} selected={form.source === s} onClick={() => u("source", s)} />)}</div>
                </div>
                {type !== "client" && (
                <div style={{ marginTop: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Lead Rating</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{RATINGS.map(r => <Chip key={r.v} label={r.v} selected={form.rating === r.v} onClick={() => u("rating", r.v)} color={r.color} bg={r.bg} />)}</div>
                </div>
                )}
                <div style={{ marginTop: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Time Zone</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{TIMEZONES.map(t => <Chip key={t} label={t} selected={form.timezone === t} onClick={() => u("timezone", t)} />)}</div>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Practice Details</h3>
                <div style={{ marginBottom: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Practice Type</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{PRACTICE_TYPES.map(p => <Chip key={p} label={p} selected={form.practice_type === p} onClick={() => u("practice_type", p)} />)}</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                  <div><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>City</label>
                    <input className="input" value={form.city} onChange={e => u("city", e.target.value)} placeholder="e.g., Houston" data-testid="lead-city-input" /></div>
                  <div><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>State</label>
                    <select className="input" value={form.state} onChange={e => u("state", e.target.value)} data-testid="lead-state-select">
                      <option value="">Select state...</option>
                      {["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map(s => <option key={s} value={s}>{s}</option>)}
                    </select></div>
                </div>
                <div style={{ marginBottom: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Implementation Timeline</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{IMPL_TIMELINES.map(t => <Chip key={t} label={t} selected={form.impl_timeline === t} onClick={() => u("impl_timeline", t)} />)}</div>
                </div>
                <div style={{ marginBottom: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Specialty</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{SPECIALTIES.map(s => <Chip key={s} label={s} selected={form.specialties.includes(s)} onClick={() => toggle("specialties", s)} />)}</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 14 }}>
                  {[["provider_md","MDs"],["provider_np","NPs"],["provider_pa","PAs"]].map(([k,l]) => (
                    <div key={k}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{l}</label>
                      <select className="input" value={form[k]} onChange={e => u(k, e.target.value)}>
                        {["0","1","2","3","4","5","6-10","11-20","20+"].map(v => <option key={v} value={v}>{v}</option>)}
                      </select></div>
                  ))}
                </div>
                <div><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Current EHR</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{CURRENT_EHRS.map(e => <Chip key={e} label={e} selected={form.current_ehrs.includes(e)} onClick={() => toggle("current_ehrs", e)} />)}</div>
                </div>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && type !== "client" && (
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 650, marginBottom: 4 }}>Pain Points & Challenges</h3>
                <p style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 16 }}>Tap what's hurting them — Claude maps solutions automatically.</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {PAIN_POINTS.map(pp => (
                    <button key={pp.id} className="card" onClick={() => toggle("pain_points", pp.label)}
                      style={{ textAlign: "left", cursor: "pointer", padding: 12, border: form.pain_points.includes(pp.label) ? "1.5px solid var(--green)" : undefined, background: form.pain_points.includes(pp.label) ? "var(--green-light)" : undefined }}>
                      <AlertTriangle style={{ width: 14, height: 14, color: form.pain_points.includes(pp.label) ? "var(--green)" : "var(--text-tertiary)", marginBottom: 4 }} />
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>{pp.label}</div>
                      <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{pp.solution}</div>
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Additional notes</label>
                  <textarea className="input" rows={3} value={form.pain_notes} onChange={e => u("pain_notes", e.target.value)} style={{ marginTop: 4 }} /></div>
              </div>
            )}

            {/* Step 3 for Clients (simplified) */}
            {step === 3 && type === "client" && (
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 650, marginBottom: 4 }}>Client details</h3>
                <p style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 16 }}>Add any additional information about this existing client.</p>
                <div style={{ marginBottom: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Products / Services</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{PRODUCTS.map(p => <Chip key={p} label={p} selected={form.products.includes(p)} onClick={() => toggle("products", p)} />)}</div>
                </div>
                <div style={{ marginBottom: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Interest Level</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{INTEREST_LEVELS.map(l => <Chip key={l} label={l} selected={form.interest_level === l} onClick={() => u("interest_level", l)} />)}</div>
                </div>
                <div style={{ marginTop: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Notes</label>
                  <textarea className="input" rows={4} value={form.notes} onChange={e => u("notes", e.target.value)} placeholder="Contract details, key contacts, special requirements..." style={{ marginTop: 4 }} /></div>
              </div>
            )}

            {/* Step 4 */}
            {step === 4 && (
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Products, Budget & Demo</h3>
                <div style={{ marginBottom: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Products of Interest</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{PRODUCTS.map(p => <Chip key={p} label={p} selected={form.products.includes(p)} onClick={() => toggle("products", p)} />)}</div>
                </div>
                <div style={{ marginBottom: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Budget Range per month</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{BUDGETS.map(b => <Chip key={b} label={b} selected={form.budget === b} onClick={() => u("budget", b)} />)}</div>
                </div>
                <div style={{ marginBottom: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Pricing Fit</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{PRICING_FIT.map(p => <Chip key={p} label={p} selected={form.pricing_fit === p} onClick={() => u("pricing_fit", p)} />)}</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                  <div><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Demo Date & Time</label>
                    <input className="input" type="datetime-local" value={form.demo_date} onChange={e => u("demo_date", e.target.value)} style={{ marginTop: 4 }} /></div>
                  <div><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Expected Close Date</label>
                    <input className="input" type="date" value={form.expected_close} onChange={e => u("expected_close", e.target.value)} style={{ marginTop: 4 }} /></div>
                </div>
                <div style={{ marginBottom: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Next Action <span style={{ color: "var(--text-tertiary)", fontWeight: 400 }}>(select one or more)</span></label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{NEXT_ACTIONS.map(a => {
                    // Iter 193 — Stephanie #7d003f61: rep needed to pick
                    // multiple next actions on a completed demo (e.g.
                    // "Send Quote + Resources" AND "Schedule Follow-Up
                    // Demo"). Stored as a comma-separated string in
                    // next_action so the backend schema (Optional[str])
                    // doesn't need to change.
                    const selectedList = (form.next_action || "").split(",").map(s => s.trim()).filter(Boolean);
                    const isSelected = selectedList.includes(a);
                    const toggle = () => {
                      const next = isSelected
                        ? selectedList.filter(x => x !== a)
                        : [...selectedList, a];
                      u("next_action", next.join(", "));
                    };
                    return <Chip key={a} label={a} selected={isSelected} onClick={toggle} />;
                  })}</div>
                </div>
              </div>
            )}

            {/* Step 5 */}
            {step === 5 && (
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Claude AI Insights</h3>
                {aiLoading ? (
                  <div style={{ textAlign: "center", padding: 40 }}>
                    <Loader2 style={{ width: 24, height: 24, color: "var(--purple)", animation: "spin 1s linear infinite", margin: "0 auto 8px" }} />
                    <p style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Claude is analyzing this lead...</p>
                  </div>
                ) : aiResult ? (
                  <div>
                    <div className="card" style={{ background: "#f8f5ff", border: "0.5px solid rgba(84,72,168,0.15)", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                        <Sparkles style={{ width: 14, height: 14, color: "var(--purple)" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--purple)" }}>Claude AI · Product Fit Analysis</span>
                      </div>
                      {(aiResult.top_products || []).map((p, i) => (
                        <div key={i} style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{p.product}</div>
                            <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{p.reason}</div>
                          </div>
                          <span className="tag tag-purple">{p.match_pct}%</span>
                        </div>
                      ))}
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        <span className="tag tag-blue">AI Score: {aiResult.ai_score}</span>
                        <span className={`tag ${aiResult.temperature === "Hot" ? "tag-red" : aiResult.temperature === "Warm" ? "tag-amber" : "tag-blue"}`}>{aiResult.temperature}</span>
                      </div>
                      {aiResult.key_insight && <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 8, background: "var(--surface)", padding: 8, borderRadius: 6 }}>{aiResult.key_insight}</p>}
                      {aiResult.next_action && <p style={{ fontSize: 12, color: "var(--blue)", fontWeight: 500, marginTop: 6 }}>Recommended: {aiResult.next_action}</p>}
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: 30 }}>
                    <Sparkles style={{ width: 24, height: 24, color: "var(--purple)", margin: "0 auto 8px" }} />
                    <p style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 12 }}>Click below to get AI insights for this lead</p>
                    <button className="btn btn-primary" onClick={fetchAI}><Sparkles style={{ width: 12, height: 12 }} /> Run Claude AI Analysis</button>
                  </div>
                )}
                <div style={{ marginTop: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Interest Level</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{INTEREST_LEVELS.map(l => <Chip key={l} label={l} selected={form.interest_level === l} onClick={() => u("interest_level", l)} />)}</div>
                </div>
                <div style={{ marginTop: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Engagement Level</label>
                  <div className="chip-group" style={{ marginTop: 4 }}>{ENGAGEMENT_LEVELS.map(l => <Chip key={l} label={l} selected={form.engagement_level === l} onClick={() => u("engagement_level", l)} />)}</div>
                </div>
                <div style={{ marginTop: 14 }}><label style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Notes</label>
                  <textarea className="input" rows={3} value={form.notes} onChange={e => u("notes", e.target.value)} placeholder="Key decision makers, special requirements, meeting highlights..." style={{ marginTop: 4 }} /></div>
              </div>
            )}

            {/* Footer */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, paddingTop: 16, borderTop: "0.5px solid var(--border)" }}>
              <div>{step > 1 && <button className="btn" onClick={() => setStep(step - 1)}><ArrowLeft style={{ width: 12, height: 12 }} /> Back</button>}</div>
              <div style={{ display: "flex", gap: 4 }}>
                {(type === "client" ? [1,2,3] : [1,2,3,4,5]).map(i => (
                  <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: i <= step ? "var(--blue)" : "var(--gray-light)" }} />
                ))}
              </div>
              <div>
                {(type === "client" ? step < 3 : step < 5) ? (
                  <button className="btn btn-primary" onClick={() => {
                    if (step === 1 && !validateStep1()) return;
                    if (type !== "client" && step === 4) fetchAI(); setStep(step + 1);
                  }} disabled={!canNext}>
                    Continue <ArrowRight style={{ width: 12, height: 12 }} />
                  </button>
                ) : (type === "client" && step === 3) || step === 5 ? (
                  <button className="btn btn-primary" onClick={saveLead} disabled={saving}>
                    {saving ? <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> : <Check style={{ width: 12, height: 12 }} />}
                    {saving ? "Saving..." : type === "client" ? "Save Client" : "Save Lead"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
